/* eslint-disable @angular-eslint/no-output-on-prefix */
// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Deal, DealStatuses, JacketType, VehicleType } from '@interfaces/deal.interfaces';
import { DealDocuments, IdCheckStatus, IdVerification } from '@interfaces/document.interfaces';
import { ErrorService } from '@services/error.service';
import { SuccessModalService } from '@services/success-modal.service';
import { UserService } from '@services/user.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { catchError, firstValueFrom, takeWhile } from 'rxjs';
import { EditUserInfoModalComponent } from 'src/app/modals/edit-user-info-modal/edit-user-info-modal.component';
import { SuccessModalComponent } from 'src/app/modals/success-modal/success-modal.component';
import { phoneImporter } from 'src/app/utils/form-utils';
import { getImage, getVehicleDisplayDescription, showMissingImage } from 'src/app/utils/utils';
import { User, UserTypes } from '../../../interfaces/user.interfaces';
import { AuditTrailModalComponent } from '../../../modals/audit-trail-modal/audit-trail-modal.component';
import { EditVehicleInfoModalComponent } from '../../../modals/edit-vehicle-info-modal/edit-vehicle-info-modal.component';
import { DealsService } from '../../../services/deals.service';
import { IdUnverifiedModalComponent } from 'src/app/modals/id-unverified-modal/id-unverified-modal.component';
import { getIdVerificationClass } from 'src/app/utils/document-utils';
import { DealershipService } from '../../../services/dealership.service';
import { DocumentsService } from '@services/documents.service';
import { EditDealInfoModalComponent } from '../edit-deal-Info/edit-deal-info-modal.component';
import { SettingsService } from '@services/settings.service';
import { getValueFromString } from '../../../utils/parse-utils';
import { DealerSettings } from '@interfaces/dealer.interfaces';
import { DealerConfigSettings } from '@interfaces/settings.interfaces';
import { SharedService } from '@services/shared.service';
import { ModalSize } from '@interfaces/shared.interfaces';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { ReasonForRequestStatus } from '@interfaces/vehicle.interfaces';
import { SnackbarService } from '@services/snackbar.service';

@Component({
    selector: 'deal-info',
    templateUrl: './deal-info.component.html',
    styleUrls: ['./deal-info.component.scss'],
})
export class DealInfoComponent implements OnDestroy, OnInit, OnChanges {
    @Input() deal: Deal = null;
    @Input() flexWidth = false;
    @Input() buyerIdVerificationResults: IdVerification = null;
    @Input() cobuyerIdVerificationResults: IdVerification = null;
    @Input() dealDocs: DealDocuments;
    @Input() disableSubmitForReview: any = null;
    @Input() showSubmitForReviewForBuyer = false;
    @Input() showSubmitForReviewForCoBuyer = false;

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onUserEdit = new EventEmitter<any>();
    @Output() onDealEdit = new EventEmitter<number>();
    @Output() onSubmitReload = new EventEmitter<any>();
    @Output() onShowIdVerificationResultsDocument = new EventEmitter<number>();
    public canEditCustomer = false;
    public hasSuccessfulBuyerLogon = false;
    public hasSuccessfulCoBuyerLogon = false;

    private isComponentAlive = true;
    public isLoading = false;
    loadingMessage: string = null;
    tenantSupportPhone = '';
    dealerConfigSettings: DealerSettings[] = [];
    vehicleImage: string | null = null;
    protected readonly ReasonForRequestStatuses = Object.entries(ReasonForRequestStatus).map(([key, value]) => ({ key, value }));
    protected readonly VehicleType = VehicleType;
    protected readonly JacketType = JacketType;

    constructor(
        private readonly deviceService: DeviceDetectorService,
        private readonly dialog: MatDialog,
        private readonly errorService: ErrorService,
        private readonly successService: SuccessModalService,
        private readonly userService: UserService,
        private readonly dealService: DealsService,
        private readonly dealershipService: DealershipService,
        private readonly documentService: DocumentsService,
        private readonly settingsService: SettingsService,
        private readonly sharedService: SharedService,
        private readonly snackbarService: SnackbarService
    ) {}

    get vehicleDisplayDescription(): string {
        return getVehicleDisplayDescription(this.deal?.vehicle?.make, this.deal?.vehicle?.model, this.deal?.vehicle?.year);
    }

    get tradeInVehicleDisplayDescription(): string {
        return getVehicleDisplayDescription(this.deal?.tradeInVehicle?.make, this.deal?.tradeInVehicle?.model, this.deal?.tradeInVehicle?.year);
    }

    get showVehiclePrice(): boolean {
        const setting = this.dealerConfigSettings.find((setting) => setting.dealerConfigSettingId === DealerConfigSettings.DealVehiclePrice);

        //default is to show the price (true) if the dealer should not have the setting already for some reason
        const showPrice = setting === undefined ? true : getValueFromString(setting?.settingValue, setting?.dataTypeId);

        return showPrice;
    }

    get isDealOpen(): boolean {
        return this.deal.statusId === DealStatuses.Open;
    }

    get isJacketDeleted(): boolean {
        return this.deal.statusId === DealStatuses.Deleted;
    }

    get isDealJacketType(): boolean {
        return this.deal.jacketTypeId === JacketType.Deal;
    }

    get isDocumentJacketType(): boolean {
        return this.deal.jacketTypeId === JacketType.Document;
    }

    get isStorageJacketType(): boolean {
        return this.deal.jacketTypeId === JacketType.Storage;
    }

    get dealOrJacket(): string {
        return this.isDealJacketType ? 'Deal' : 'Jacket';
    }

    get jacketStatus(): string {
        return this.deal.statusId === DealStatuses.Completed ? 'Closed' : DealStatuses[this.deal.statusId];
    }

    get reasonForRequest(): string {
        return this.ReasonForRequestStatuses.find(({ key, value }) => value === this.deal.reasonForRequest)?.key;
    }

    get financeManager(): string {
        return this.deal.dealerContact?.userId ? this.deal.dealerContact.firstName + ' ' + this.deal.dealerContact.lastName : 'N/A';
    }

    get tradeInExists(): boolean {
        return Object.values({ ...this.deal.tradeInVehicle, ...this.deal.tradeInVehicle.vehicleExt, vehicleExt: null }).filter((val) => !!val).length > 2;
        // Dealer ID and Price are the only fields that will exist if the trade-in does not exist
    }

    ngOnInit(): void {
        // can dealer edit the buyer and cobuyer information
        this.dealershipService.dealer$.pipe(takeWhile(() => this.isComponentAlive)).subscribe(async (dealer) => {
            ({ supportPhone: this.tenantSupportPhone } = await this.settingsService.getOemSettings(dealer.oemCode));
        });

        //get dealerSettings
        this.dealershipService
            .getCurrentDealerAppSettings$()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((configSettings) => {
                this.dealerConfigSettings = configSettings;
            });

        this.checkDealStatus();
        this.getVehicleImage();
    }

    ngOnChanges(): void {
        this.checkDealStatus();
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }

    private checkDealStatus() {
        this.setCanEditCustomer(this.deal.statusId === DealStatuses.Open);
        this.setCanResendLink();
    }

    idVerificationClass(verif: IdVerification) {
        const pointer = this.resultsAvailable(verif) ? 'pointer ' : '';
        return pointer + getIdVerificationClass(verif) + ' DriverseLicense';
    }

    idVerificationResultsClick(verif: IdVerification) {
        if (!this.resultsAvailable(verif)) {
            // failsafe
            return;
        }

        if (!verif.resultsDocumentId) {
            this.dialog.open(IdUnverifiedModalComponent, {
                width: '900px',
                maxHeight: '95%',
                closeOnNavigation: true,
                data: {
                    deal: this.deal,
                    idVerificationResults: verif,
                    tenantSupportPhone: this.tenantSupportPhone,
                },
            });
        } else {
            this.onShowIdVerificationResultsDocument.emit(verif.resultsDocumentId);
        }
    }

    public resultsAvailable(verif: IdVerification) {
        if (!verif || IdCheckStatus.Pending === verif.verificationStatus) {
            return false;
        }

        return true;
    }

    public editDealInfo(): void {
        this.dialog
            .open(EditDealInfoModalComponent, {
                width: this.deviceService.isDesktop() ? '700px' : '100vw',
                closeOnNavigation: false,
                data: {
                    deal: this.deal,
                },
            })
            .afterClosed()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((val: Deal) => {
                this.isLoading = true;
                if (val) {
                    this.dealService
                        .updateDealInfo(val.dealerId, val.dealId, val.dealerDealNumber, val.reasonForRequest, val.dealerContact.userId, val.deleteReason)
                        .pipe(
                            takeWhile(() => this.isComponentAlive),
                            catchError((error) => {
                                this.isLoading = false;
                                this.errorService.showGenericError();
                                throw error;
                            })
                        )
                        .subscribe((result: Deal) => {
                            this.deal.dealerDealNumber = result.dealerDealNumber;
                            this.isLoading = false;
                            this.onDealEdit.emit(this.deal.dealId);

                            this.snackbarService.openSnackbar('success', `${this.isDealJacketType ? 'Deal' : 'Jacket'} information has been updated.`);
                        });
                } else {
                    this.isLoading = false;
                }
            });
    }

    public editBuyerInfo(userType: 'buyer' | 'coBuyer'): void {
        const label = 'buyer' === userType ? 'Buyer' : 'Co-Buyer';
        const offLimitsUserIds = this.getOfflimitsUserIds(userType);

        this.dialog
            .open(EditUserInfoModalComponent, {
                width: this.deviceService.isDesktop() ? '700px' : '100vw',
                closeOnNavigation: false,
                data: {
                    user: this.deal[userType],
                    title: `Edit ${label} Info`,
                    dealerUpdate: true,
                    allowExistingUser: true,
                    offLimitsUserIds,
                },
            })
            .afterClosed()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((val) => {
                if (val) {
                    //check if the dealer is trying to swap the buyer/cobuyer without using the swap button
                    if (userType === 'buyer' && val.userId === this.deal.coBuyer.userId) {
                        const error = {
                            title: 'Buyer Information Cannot Be Changed to Co-Buyer',
                            message:
                                'This person is identified as the co-buyer on the deal. If you want to switch the buyer and co-buyer, use the Swap Buyer and Co-Buyer button.',
                        };

                        this.errorService.showError(error);
                        return;
                    } else if (userType === 'coBuyer' && val.userId === this.deal.buyer.userId) {
                        const error = {
                            title: 'Co-Buyer Information Cannot Be Changed to Buyer',
                            message:
                                'This person is identified as the buyer on the deal. If you want to switch the buyer and co-buyer, use the Swap Buyer and Co-Buyer button.',
                        };

                        this.errorService.showError(error);
                        return;
                    }

                    this.isLoading = true;
                    this.saveDealerChanges(this.deal[userType], val, userType);
                }
            });
    }

    // When editing a buer or cobuyer we need to make sure the user cannot
    // set the buyer and coBuyer to the same account
    public getOfflimitsUserIds(userType: 'buyer' | 'coBuyer'): number[] {
        if (userType === 'buyer' && this.deal?.coBuyer?.userId) {
            return [this.deal.coBuyer.userId];
        } else if (userType === 'coBuyer') {
            return [this.deal.buyer.userId];
        } else {
            return [];
        }
    }

    public editVehicleInfo(type: VehicleType = VehicleType.Vehicle): void {
        this.dialog
            .open(EditVehicleInfoModalComponent, {
                width: this.deviceService.isDesktop() ? '700px' : '100vw',
                closeOnNavigation: false,
                data: {
                    deal: this.deal,
                    title: `Edit ${type === VehicleType.TradeIn ? 'Trade-In ' : ''}Vehicle Information`,
                    dealerUpdate: true,
                    search: true,
                    vehicleType: type,
                },
            })
            .afterClosed()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((val) => {
                if (val) {
                    this.isLoading = true;
                    this.saveVehicleChanges(this.deal, val, type);
                }
            });
    }

    public async getVehicleImage(): Promise<any> {
        await getImage(this.deal).then((image) => {
            if (image.includes('https') || image.includes('assets/')) {
                this.vehicleImage = image;
            } else {
                this.vehicleImage = 'data:image/webp;base64, ' + image;
            }
        });
    }

    public importPhone(phone: string): string {
        if (!phone) {
            return 'N/A';
        }
        const phoneNum = phoneImporter(phone);
        return `(${phoneNum.substring(0, 3)}) ${phoneNum.substring(3, 6)}-${phoneNum.substring(6, 10)}`;
    }

    public resendLink(userType: string): void {
        const email = userType === 'buyer' ? this.deal.buyer.email : this.deal.coBuyer.email;

        this.userService
            .sendResetUserLink(email)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((val) => {
                if (val && val.data.includes('Success')) {
                    this.dialog.open(SuccessModalComponent, {
                        width: '700px',
                        data: {
                            title: 'Welcome Link Sent',
                            content: 'The welcome link was sent successfully.',
                        },
                    });
                } else {
                    this.errorService.showError('User has already logged in. Welcome link cannot be resent.');
                }
            });
    }

    async removeTradeIn(deal: any, dealerId: number): Promise<void> {
        this.dealService
            .saveVehicle(deal.dealId, { dealerId: dealerId }, VehicleType.TradeIn)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.isLoading = false;
                    this.errorService.showGenericError();
                    throw error;
                })
            )
            .subscribe((result) => {
                if (result) {
                    this.deal.tradeInVehicle = result;
                    this.isLoading = false;
                    this.snackbarService.openSnackbar('success', 'The trade-in vehicle has been removed.');
                } else {
                    this.isLoading = false;
                    this.errorService.showGenericError();
                }
            });
    }

    private async saveVehicleChanges(deal: any, vehicleChanges: any, type: VehicleType): Promise<void> {
        this.dealService
            .saveVehicle(deal.dealId, vehicleChanges, type)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.isLoading = false;
                    this.errorService.showGenericError();
                    throw error;
                })
            )
            .subscribe((result) => {
                if (result) {
                    if (type === VehicleType.Vehicle) {
                        this.deal.vehicle = result;
                    } else {
                        this.deal.tradeInVehicle = result;
                    }
                    this.isLoading = false;

                    this.snackbarService.openSnackbar(
                        'success',
                        type === VehicleType.TradeIn ? 'The trade-in vehicle information has been updated.' : 'The vehicle information has been updated.'
                    );
                } else {
                    this.isLoading = false;
                    this.errorService.showGenericError();
                }
            });
    }

    private async saveDealerChanges(dealUser: any, userChanges: any, userType: 'buyer' | 'coBuyer'): Promise<void> {
        const phone = userChanges.phone;

        this.userService
            .adminUpdateUserOnDeal$(
                userChanges.userId,
                userChanges.logonId,
                userChanges.email,
                userChanges.firstName,
                userChanges.lastName,
                phone,
                this.deal.dealId,
                userType,
                userChanges.middleName
            )
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.isLoading = false;
                    this.errorService.showGenericError();
                    throw error;
                })
            )
            .subscribe((result) => {
                // copy of the user is returned, so if no ID, assume error
                if (result && result.userId) {
                    this.deal[userType] = result;
                    this.isLoading = false;
                    this.showUserFeedback(userType);
                    this.onDealEdit.emit(this.deal.dealId);
                    this.onUserEdit.emit();
                } else {
                    this.isLoading = false;
                    this.errorService.showGenericError();
                }
            });
    }

    setCanEditCustomer(canEdit: boolean) {
        this.canEditCustomer = canEdit;
    }

    setCanResendLink() {
        if (this.deal.buyer.userId !== null) {
            this.checkUserLoginStatus(this.deal.buyer.userId, 'buyer');
        }

        if (this.deal.coBuyer.userId != null) {
            this.checkUserLoginStatus(this.deal.coBuyer.userId, 'cobuyer');
        }
    }

    setCanResendCustomerLink(canResend: boolean, type: 'buyer' | 'cobuyer') {
        type === 'buyer' ? (this.hasSuccessfulBuyerLogon = canResend) : (this.hasSuccessfulCoBuyerLogon = canResend);
    }

    public showMissingImage($event): any {
        showMissingImage($event);
    }

    private showUserFeedback(type: 'buyer' | 'coBuyer') {
        this.snackbarService.openSnackbar(
            'success',
            type === 'buyer' ? 'The buyer information has been updated.' : 'The co-buyer information has been updated.'
        );
    }

    public viewAuditTrailForVehicle(vehicleType: VehicleType = VehicleType.Vehicle): void {
        this.dealService
            .getAuditTrailForVehicle(this.deal.dealId, vehicleType)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((trail) => {
                if (trail.length !== 0) {
                    const modalData = { deal: this.deal, showDiff: true, trail: trail, uniqueTitle: undefined };

                    if (this.deal.jacketTypeId === JacketType.Document) modalData.uniqueTitle = 'Document Jacket ID';

                    this.dialog.open(AuditTrailModalComponent, {
                        width: '900px',
                        closeOnNavigation: true,
                        data: modalData,
                    });
                }
            });
    }

    public viewDealAuditTrail(): void {
        this.dealService
            .getAuditTrailForDeal(this.deal.dealerId, this.deal.dealId)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.errorService.showError(
                        `We don't have any data for this audit trail. If you think this is an error, please contact ${this.tenantSupportPhone}`
                    );

                    throw error;
                })
            )
            .subscribe((trail) => {
                const modalData = { deal: this.deal, showDiff: true, trail: trail, uniqueTitle: undefined };

                if (this.deal.jacketTypeId === JacketType.Document) modalData.uniqueTitle = 'Document Jacket ID';

                this.dialog.open(AuditTrailModalComponent, {
                    width: '900px',
                    closeOnNavigation: true,
                    data: modalData,
                });
            });
    }

    public viewAuditTrail(userType: any): void {
        const userTypes = userType === 'buyer' ? UserTypes.Buyer : UserTypes.CoBuyer;

        this.dealService
            .getAuditTrailForUser(null, this.deal.dealId, userTypes)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.errorService.showError(
                        `We don't have any data for this audit trail. If you think this is an error, please contact ${this.tenantSupportPhone}`
                    );

                    throw error;
                })
            )
            .subscribe((trail) => {
                const modalData = { deal: this.deal, showDiff: true, trail: trail, uniqueTitle: undefined };

                if (this.deal.jacketTypeId === JacketType.Document) modalData.uniqueTitle = 'Document Jacket ID';

                this.dialog.open(AuditTrailModalComponent, {
                    width: '900px',
                    closeOnNavigation: true,
                    data: modalData,
                });
            });
    }

    private checkUserLoginStatus(userId: number, type: 'buyer' | 'cobuyer') {
        this.userService
            .getUserByUserId$(this.deal.dealerId, userId)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.setCanResendCustomerLink(false, type);
                    throw error;
                })
            )
            .subscribe((user) => {
                if (user) {
                    //check firstSuccessfullLogon date to see if the user has every successfully logged on including successfull MFA
                    this.setCanResendCustomerLink(user.firstSuccessfulLogon === null ? true : false, type);
                }
            });
    }

    submitForValidation(doc: IdVerification) {
        const phrases = [
            'Retrieving the ID Scan From the DDJ Secure Vault',
            'Performing OCR on the ID front...',
            'Decoding the back of ID barcode...',
            'Almost Complete...',
        ];
        let currentPhrase = 0;
        let timer: NodeJS.Timeout = null;
        const incrementPhrase = () => {
            this.loadingMessage = phrases[currentPhrase];
            currentPhrase++;

            if (currentPhrase >= phrases.length) {
                // this is the last message, stop the timer and leave this message visible
                clearInterval(timer);
            }
        };
        // start the timer
        timer = setInterval(incrementPhrase, 4567); // 4.5(ish) seconds between steps
        // set the initial phrase
        incrementPhrase();

        this.isLoading = true;
        this.documentService
            .submitForValidation$(this.deal.dealerId, this.deal.dealId, doc.documentId)
            .pipe(
                takeWhile(() => true === this.isComponentAlive),
                catchError((error) => {
                    this.isLoading = false;
                    return error;
                })
            )
            .subscribe((success) => {
                clearInterval(timer);
                this.loadingMessage = null;
                this.isLoading = false;
                // let our container know it should update
                this.onUserEdit.emit();
                this.onSubmitReload.emit();
            });
    }

    private async removeCobuyerFromJacket(dealId: number, cobuyer: User): Promise<void> {
        this.isLoading = true;

        const updatedDeal = await firstValueFrom(this.dealService.removeCoBuyerFromJacket(dealId, cobuyer.userId));

        this.isLoading = false;

        if (updatedDeal)
            this.dialog
                .open(SuccessModalComponent, {
                    width: this.sharedService.getModalWidth(ModalSize.Medium) + 'px',
                    closeOnNavigation: true,
                    data: {
                        title: 'Co-Buyer Removed',
                        richContent: `<b>${cobuyer.firstName} ${cobuyer.lastName}</b> has been removed from the jacket.`,
                        btnText: 'Okay',
                    },
                })
                .afterClosed()
                .pipe(takeWhile(() => this.isComponentAlive))
                .subscribe(() => {
                    this.onDealEdit.emit(this.deal.dealId);
                });
    }

    public removeCobuyer(): void {
        const cobuyer = this.deal.coBuyer;

        this.dialog
            .open(ConfirmModalComponent, {
                width: this.sharedService.getModalWidth(ModalSize.Medium) + 'px',
                closeOnNavigation: true,
                data: {
                    title: 'Removing Co-Buyer',
                    richMessage: `Are you sure you want to remove <b>${cobuyer.firstName} ${cobuyer.lastName}</b>? If you have not already done so, you may want to also delete any documents associated with the Co-Buyer from the jacket.`,
                    btnText: 'Continue',
                },
            })
            .afterClosed()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((res) => {
                if (res) this.removeCobuyerFromJacket(this.deal.dealId, cobuyer);
            });
    }
}
